import { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCartList, addToCart, deleteSpecificCartItem, clearCart as clearCartAPI, PLATFORMS } from "../../../../data/cart";
import { parseQty } from "../utils/cartCalculations";
import { notifyPromotionMessage } from "../../../../utils/promotionMessageToast";
import {
  getCartItemIndexFromLookup,
  CART_ITEM_LOOKUP_ROLE_OFFER,
  CART_ITEM_LOOKUP_ROLE_PAID,
  isCartLineOfferDisplayRole,
} from "../../Promotion/utils/promotionOfferProductsShared";

const normalizeOrderTypeForMatch = (t) => {
  const s = String(t || "Box").trim().toLowerCase();
  if (s === "piece") return "Piece";
  return "Box";
};

const normalizePromoIdForKey = (pid) => {
  if (pid === null || pid === undefined || pid === "" || String(pid).toLowerCase() === "null") {
    return "";
  }
  return String(pid);
};

const cartLineRoleSuffix = (item) =>
  isCartLineOfferDisplayRole(item) ? "offer" : "paid";

const cartLineKey = (item) =>
  [
    String(item.product_id),
    normalizeOrderTypeForMatch(item.order_type || item.type),
    String(item.is_promoted ?? "0"),
    normalizePromoIdForKey(item.promo_id),
    cartLineRoleSuffix(item),
  ].join("|");

const cartLineWeakKey = (item) =>
  [
    String(item.product_id),
    normalizeOrderTypeForMatch(item.order_type || item.type),
    cartLineRoleSuffix(item),
  ].join("|");

const scalePromoAmount = (raw, fromQty, toQty) => {
  const amount = parseFloat(raw);
  if (!Number.isFinite(amount) || amount <= 0) return raw ?? 0;
  if (!(fromQty > 0) || !(toQty > 0)) return 0;
  return Number(((amount * toQty) / fromQty).toFixed(2));
};

const parseItemDiscountOnly = (item) => {
  if (!item) return null;
  const keys = ["productWiseDiscountValue", "productwiseDiscountValue", "item_discount", "discount"];
  for (let i = 0; i < keys.length; i += 1) {
    const raw = item[keys[i]];
    if (raw === undefined || raw === null || String(raw).trim() === "") continue;
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
};

const parsePromoLineAmount = (item) => {
  if (!item) return 0;
  const keys = ["line_discount", "promo_disc_amount", "promo_discount_amount", "promotion_discount"];
  for (let i = 0; i < keys.length; i += 1) {
    const n = parseFloat(item[keys[i]]);
    if (Number.isFinite(n) && n > 0.0001) return n;
  }
  return 0;
};

const sameStr = (a, b) => String(a ?? "") === String(b ?? "");

const cartLineUnchanged = (existingItem, backendItem) => {
  if (!existingItem || !backendItem) return false;
  return (
    sameStr(existingItem.qty, backendItem.qty) &&
    sameStr(existingItem.price ?? existingItem.unit_price, backendItem.price ?? backendItem.unit_price) &&
    sameStr(existingItem.unit_price ?? existingItem.price, backendItem.unit_price ?? backendItem.price) &&
    sameStr(existingItem.discount, backendItem.discount) &&
    sameStr(existingItem.sub_total ?? existingItem.subtotal, backendItem.sub_total ?? backendItem.subtotal) &&
    sameStr(existingItem.is_promoted ?? "0", backendItem.is_promoted ?? "0") &&
    normalizePromoIdForKey(existingItem.promo_id) === normalizePromoIdForKey(backendItem.promo_id) &&
    sameStr(existingItem.promo_type, backendItem.promo_type) &&
    sameStr(existingItem.id, backendItem.id) &&
    sameStr(existingItem.promotion_discount, backendItem.promotion_discount) &&
    sameStr(existingItem.line_discount, backendItem.line_discount)
  );
};

const buildCartLineLookupMaps = (items) => {
  const byExact = new Map();
  const byWeak = new Map();
  if (!Array.isArray(items)) {
    return { byExact, byWeak };
  }
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!item) continue;
    byExact.set(cartLineKey(item), item);
    const weak = cartLineWeakKey(item);
    if (!byWeak.has(weak)) {
      byWeak.set(weak, item);
    }
  }
  return { byExact, byWeak };
};

export const mergeCartListFromAddResponse = (old, data) => {
  if (!data?.data || !Array.isArray(data.data)) {
    return old || data;
  }
  if (!old) {
    return data;
  }

  const backendItems = data.data;
  const currentItems = old.data || [];
  const { byExact, byWeak } = buildCartLineLookupMaps(currentItems);

  const mergeOne = (existingItem, backendItem) => {
    if (cartLineUnchanged(existingItem, backendItem)) {
      return existingItem;
    }

    const backendUnitPrice = parseFloat(backendItem.unit_price || 0);
    const existingQty = parseQty(existingItem, existingItem.qty);
    const backendQty = parseQty(backendItem, backendItem.qty);
    const qtyChanged =
      Number.isFinite(existingQty) &&
      Number.isFinite(backendQty) &&
      Math.abs(existingQty - backendQty) > 0.0001;

    const pickPromoAmount = (key) => {
      if (backendItem[key] !== undefined && backendItem[key] !== null && backendItem[key] !== "") {
        return backendItem[key];
      }
      if (qtyChanged) {
        return 0;
      }
      return existingItem[key];
    };

    const existingDiscount = parseItemDiscountOnly(existingItem);
    let backendDiscount = parseItemDiscountOnly(backendItem);
    const promoLineAmount = parsePromoLineAmount(backendItem);
    if (
      backendDiscount !== null &&
      backendDiscount > 0.0001 &&
      promoLineAmount > 0.0001 &&
      Math.abs(backendDiscount - promoLineAmount) < 0.001
    ) {
      const existingWasSame =
        existingDiscount !== null && Math.abs(existingDiscount - backendDiscount) < 0.001;
      if (!existingWasSame) {
        backendDiscount = 0;
      }
    }
    const frontendDiscount =
      !qtyChanged && existingDiscount !== null
        ? existingDiscount
        : backendDiscount !== null
          ? backendDiscount
          : 0;
    const frontendDiscountType =
      existingItem.discount_type ||
      existingItem.productWiseDiscountType ||
      backendItem.discount_type ||
      backendItem.productWiseDiscountType ||
      backendItem.productwiseDiscountType ||
      "amount";
    const isOfferLine = isCartLineOfferDisplayRole(backendItem);
    const keepComment =
      !isOfferLine && Number.isFinite(frontendDiscount) && frontendDiscount > 0.0001;
    const resolvedComment = keepComment
      ? String(
          backendItem.comment ||
            backendItem.discount_comment ||
            existingItem.comment ||
            existingItem.discount_comment ||
            ""
        ).trim()
      : "";

    const mergedCode =
      backendItem.product_code ||
      backendItem.code ||
      existingItem.product_code ||
      existingItem.code ||
      "";
    const mergedName =
      backendItem.product_name ||
      backendItem.name ||
      backendItem.desc ||
      existingItem.product_name ||
      existingItem.name ||
      existingItem.desc ||
      "";

    return {
      ...backendItem,
      code: mergedCode,
      product_code: mergedCode,
      name: mergedName,
      product_name: mergedName,
      desc: mergedName,
      unit_price: Number.isFinite(backendUnitPrice) && backendUnitPrice !== 0
        ? String(backendUnitPrice)
        : backendItem.unit_price,
      price: backendItem.price,
      is_refund:
        backendItem.is_refund ??
        existingItem.is_refund ??
        (backendUnitPrice < 0 || parseFloat(backendItem.price) < 0 ? "1" : "0"),
      discount: Number.isFinite(frontendDiscount)
        ? String(frontendDiscount.toFixed(2))
        : backendItem.discount,
      discount_type: frontendDiscountType,
      comment: resolvedComment,
      discount_comment: resolvedComment,
      productWiseDiscountValue: Number.isFinite(frontendDiscount)
        ? String(frontendDiscount.toFixed(2))
        : (backendItem.productWiseDiscountValue ?? backendItem.productwiseDiscountValue),
      productWiseDiscountType: frontendDiscountType,
      productwiseDiscountValue:
        backendItem.productwiseDiscountValue ??
        backendItem.productWiseDiscountValue ??
        (Number.isFinite(frontendDiscount) ? String(frontendDiscount.toFixed(2)) : undefined),
      qty: String(backendItem.qty ?? existingItem.qty ?? 0),
      order_qty: String(
        backendItem.order_qty ?? backendItem.qty ?? existingItem.order_qty ?? existingItem.qty ?? 0
      ),
      sub_total: backendItem.sub_total ?? backendItem.subtotal,
      subtotal: backendItem.subtotal ?? backendItem.sub_total,
      vat: backendItem.vat ?? backendItem.tax,
      tax: backendItem.tax ?? backendItem.vat,
      sales_box_price: backendItem.sales_box_price || existingItem.sales_box_price,
      sales_piece_price: backendItem.sales_piece_price || existingItem.sales_piece_price,
      offer_box_price:
        backendItem.offer_box_price !== undefined
          ? backendItem.offer_box_price
          : existingItem.offer_box_price,
      offer_piece_price:
        backendItem.offer_piece_price !== undefined
          ? backendItem.offer_piece_price
          : existingItem.offer_piece_price,
      is_promoted:
        backendItem.is_promoted !== undefined ? backendItem.is_promoted : existingItem.is_promoted,
      promo_id: backendItem.promo_id !== undefined ? backendItem.promo_id : existingItem.promo_id,
      promo_type:
        backendItem.promo_type !== undefined ? backendItem.promo_type : existingItem.promo_type,
      promotion_discount: pickPromoAmount("promotion_discount"),
      line_discount: pickPromoAmount("line_discount"),
      discount_amount: pickPromoAmount("discount_amount"),
      total_discount: pickPromoAmount("total_discount"),
      promo_disc_amount: pickPromoAmount("promo_disc_amount"),
    };
  };

  const mergedItems = backendItems.map((backendItem) => {
    const exact = byExact.get(cartLineKey(backendItem));
    let merged;
    if (exact) {
      merged = mergeOne(exact, backendItem);
    } else {
      const weak = byWeak.get(cartLineWeakKey(backendItem));
      merged = weak ? mergeOne(weak, backendItem) : backendItem;
    }
    const isOfferLine = isCartLineOfferDisplayRole(merged);
    const disc = parseItemDiscountOnly(merged);
    const keepComment = !isOfferLine && disc !== null && disc > 0.0001;
    if (keepComment) return merged;
    if (!merged?.comment && !merged?.discount_comment) return merged;
    return { ...merged, comment: "", discount_comment: "" };
  });

  return {
    ...data,
    data: mergedItems,
    success: data.success ?? old.success,
    message: data.message ?? old.message,
    bundle_discounts:
      data.bundle_discounts !== undefined ? data.bundle_discounts : old.bundle_discounts,
  };
};

/**
 * Custom hook for cart API operations
 * @param {number|string} customerId - Customer ID
 * @param {string} platform - Platform identifier ("5" for TELE_CALLING or "4" for POS). Defaults to "5"
 * @returns {Object} Cart API methods and state
 */
export const useCartApi = (customerId, platform = PLATFORMS.TELE_CALLING, onErrorMessage = null) => {
  const queryClient = useQueryClient();
  const [pendingOperations, setPendingOperations] = useState(new Set());
  const [isSyncingCart, setIsSyncingCart] = useState(false);
  const debounceTimers = useRef({});
  const cartSyncTimerRef = useRef(null);

  const { data: cartData, isLoading: isLoadingCart, isFetching: isFetchingCart, refetch } = useQuery({
    queryKey: ["cartList", customerId, platform],
    queryFn: () => getCartList(customerId, platform),
    enabled: !!customerId,
    staleTime: 0,
    gcTime: 1000 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const fetchCartListQuery = useCallback(async () => {
    if (!customerId) {
      return null;
    }
    return queryClient.fetchQuery({
      queryKey: ["cartList", customerId, platform],
      queryFn: () => getCartList(customerId, platform),
    });
  }, [customerId, platform, queryClient]);

  const syncCartListAfterMutation = useCallback(async () => {
    setIsSyncingCart(true);
    try {
      const cartRes = await fetchCartListQuery();
      notifyPromotionMessage(cartRes);
      if (cartRes) {
        // Merge against whatever is in the cache AT WRITE TIME (via the updater-function
        // form), not a snapshot captured before this await - another mutation/sync can
        // complete while fetchCartListQuery() is in flight, and merging against a stale
        // snapshot here would silently overwrite its (correct, newer) result.
        queryClient.setQueryData(
          ["cartList", customerId, platform],
          (old) => mergeCartListFromAddResponse(old, cartRes)
        );
      }
      return cartRes;
    } catch {
      queryClient.invalidateQueries({ queryKey: ["cartList", customerId, platform] });
      return null;
    } finally {
      setIsSyncingCart(false);
    }
  }, [customerId, platform, fetchCartListQuery, queryClient]);

  const scheduleCartListSync = useCallback(
    (delayMs = 1200) => {
      if (cartSyncTimerRef.current) {
        clearTimeout(cartSyncTimerRef.current);
      }
      cartSyncTimerRef.current = setTimeout(() => {
        cartSyncTimerRef.current = null;
        syncCartListAfterMutation();
      }, delayMs);
    },
    [syncCartListAfterMutation]
  );

  const addToCartMutation = useMutation({
    mutationFn: (data) => addToCart(data, platform),
    retry: false,
    onMutate: async (variables) => {
      setPendingOperations(prev => new Set(prev).add(variables.product_id));
      
      await queryClient.cancelQueries({ queryKey: ["cartList", customerId, platform] });
      
      const previousCartData = queryClient.getQueryData(["cartList", customerId, platform]);
      
      queryClient.setQueryData(["cartList", customerId, platform], (old) => {
        if (!old || !old.data || !Array.isArray(old.data)) return old;
        
        const cartItems = [...old.data];
        const productId = String(variables.product_id);
        const orderType = normalizeOrderTypeForMatch(variables.piece_type);
        const isPromoted = String(variables.is_promoted || "0");
        const qtyDelta = parseFloat(variables.qty) || parseInt(variables.qty, 10) || 0;
        
        const incomingPromoType = String(variables.promo_type || "").trim().toUpperCase();
        const incomingIsOffer =
          incomingPromoType === "GET" || incomingPromoType === "FREE";
        const existingIndex = getCartItemIndexFromLookup(
          cartItems,
          productId,
          orderType,
          incomingIsOffer ? CART_ITEM_LOOKUP_ROLE_OFFER : CART_ITEM_LOOKUP_ROLE_PAID
        );
        
        if (existingIndex >= 0) {
          const existingItem = cartItems[existingIndex];
          const currentQty = parseQty(existingItem, existingItem.qty);
          
          if (qtyDelta === 0) {
            const discountType = variables.discount_type || existingItem.discount_type || existingItem.productWiseDiscountType || "amount";
            const discountValue = parseFloat(variables.discount_value !== undefined ? variables.discount_value : (existingItem.discount || existingItem.productWiseDiscountValue || 0));
            const updatedUnitPrice = variables.price !== undefined && variables.price !== null
              ? parseFloat(variables.price)
              : parseFloat(existingItem.unit_price ?? existingItem.price ?? 0);
            const priceString = Number.isFinite(updatedUnitPrice)
              ? updatedUnitPrice.toFixed(2)
              : String(existingItem.price ?? existingItem.unit_price ?? "0");

            const nextComment =
              discountValue <= 0
                ? ""
                : variables.discount_comment !== undefined
                  ? String(variables.discount_comment || "").trim()
                  : String(existingItem.discount_comment || existingItem.comment || "").trim();
            cartItems[existingIndex] = {
              ...existingItem,
              qty: String(currentQty),
              price: priceString,
              unit_price: priceString,
              sub_total: variables.sub_total !== undefined
                ? String(variables.sub_total)
                : existingItem.sub_total,
              discount: String(discountValue.toFixed(2)),
              discount_type: discountType,
              productWiseDiscountType: discountType,
              productWiseDiscountValue: discountValue,
              comment: nextComment,
              discount_comment: nextComment,
              is_promoted: variables.is_promoted !== undefined ? String(variables.is_promoted) : existingItem.is_promoted,
              promo_id: variables.promotion_id !== undefined ? variables.promotion_id : (variables.promo_id !== undefined ? variables.promo_id : existingItem.promo_id),
              promo_type: variables.promo_type !== undefined ? variables.promo_type : existingItem.promo_type
            };
          } else {
            const newQty = Math.max(0, currentQty + qtyDelta);
            
            if (newQty <= 0) {
              cartItems.splice(existingIndex, 1);
            } else {
              let basePrice = parseFloat(existingItem.unit_price || 0);
              if (!basePrice || basePrice <= 0) {
                basePrice = parseFloat(variables.price || existingItem.price || 0);
              }
              
              const discountType = variables.discount_type || existingItem.discount_type || existingItem.productWiseDiscountType || "amount";
              const discountValue = parseFloat(variables.discount_value !== undefined ? variables.discount_value : (existingItem.discount || existingItem.productWiseDiscountValue || 0));
              
              const subtotal = basePrice * newQty;
              let discountAmount = 0;
              if (discountType === "amount" || discountType === "fixed" || discountType === "£") {
                discountAmount = discountValue * newQty;
              } else {
                discountAmount = (subtotal * discountValue) / 100;
              }
              const discountedSubtotal = subtotal - discountAmount;
              const discountedPrice = discountedSubtotal / newQty;
              const vatRate = parseFloat(existingItem.tax_rate || existingItem.rate || 0);
              const vat = (discountedSubtotal * vatRate) / 100;
              
              cartItems[existingIndex] = {
                ...existingItem,
                qty: String(newQty),
                order_qty: String(newQty),
                price: String(discountedPrice.toFixed(2)),
                discount: String(discountValue.toFixed(2)),
                discount_type: discountType,
                productWiseDiscountType: discountType,
                productWiseDiscountValue: discountValue,
                sub_total: String(discountedSubtotal.toFixed(2)),
                vat: String(vat.toFixed(2)),
                tax: String(vat.toFixed(2)),
                is_promoted: variables.is_promoted !== undefined ? String(variables.is_promoted) : existingItem.is_promoted,
                promo_id: variables.promotion_id !== undefined ? variables.promotion_id : (variables.promo_id !== undefined ? variables.promo_id : existingItem.promo_id),
                promo_type: variables.promo_type !== undefined ? variables.promo_type : existingItem.promo_type,
                promotion_discount: scalePromoAmount(existingItem.promotion_discount, currentQty, newQty),
                line_discount: scalePromoAmount(existingItem.line_discount, currentQty, newQty),
                discount_amount: scalePromoAmount(existingItem.discount_amount, currentQty, newQty),
                total_discount: scalePromoAmount(existingItem.total_discount, currentQty, newQty),
                promo_disc_amount: scalePromoAmount(existingItem.promo_disc_amount, currentQty, newQty),
              };
            }
          }
        } else if (qtyDelta > 0) {
          const price = parseFloat(variables.price || 0);
          const discountType = variables.discount_type || "amount";
          const discountValue = parseFloat(variables.discount_value || 0);
          const subtotal = price * qtyDelta;
          let discountAmount = 0;
          if (discountType === "amount" || discountType === "fixed" || discountType === "£") {
            discountAmount = discountValue * qtyDelta;
          } else {
            discountAmount = (subtotal * discountValue) / 100;
          }
          const discountedSubtotal = subtotal - discountAmount;
          const vatRate = parseFloat(variables.rate || 0);
          const vat = (discountedSubtotal * vatRate) / 100;
          
          cartItems.push({
            product_id: productId,
            id: variables.id || `temp_${Date.now()}`,
            qty: String(qtyDelta),
            price: String(price.toFixed(2)),
            discount: String(discountValue.toFixed(2)),
            discount_type: discountType,
            order_type: orderType === "Piece" ? "Piece" : "Box",
            type: orderType === "Piece" ? "Piece" : "Box",
            sub_total: String(discountedSubtotal.toFixed(2)),
            vat: String(vat.toFixed(2)),
            tax: String(vat.toFixed(2)),
            rate: String(vatRate),
            tax_rate: String(vatRate),
            tax_code: variables.tax_code || "",
            name: variables.product_name || "",
            product_name: variables.product_name || "",
            code: variables.product_code || "",
            product_code: variables.product_code || "",
            is_promoted: isPromoted,
            promo_id: variables.promotion_id || variables.promo_id || null,
            promo_type: variables.promo_type || null
          });
        }
        
        return {
          ...old,
          data: cartItems
        };
      });
      
      return { previousCartData };
    },
    onError: (err, variables, context) => {
      setIsSyncingCart(false);
      if (context?.previousCartData) {
        queryClient.setQueryData(["cartList", customerId, platform], context.previousCartData);
      }
      if (err?.message) {
        if (onErrorMessage) {
          onErrorMessage(err.message, "warning");
        }
      }
    },
    onSuccess: async (data) => {
      notifyPromotionMessage(data);
      const hasCartPayload = Array.isArray(data?.data) && data.data.length > 0;
      if (hasCartPayload) {
        const previous = queryClient.getQueryData(["cartList", customerId, platform]);
        const previousCount = Array.isArray(previous?.data) ? previous.data.length : 0;
        const nextCount = data.data.length;
        if (previousCount > 1 && nextCount < previousCount && nextCount <= Math.max(2, previousCount * 0.5)) {
          await syncCartListAfterMutation();
          return;
        }
        queryClient.setQueryData(
          ["cartList", customerId, platform],
          (old) => mergeCartListFromAddResponse(old, data),
        );
        scheduleCartListSync(1200);
        return;
      }
      await syncCartListAfterMutation();
    },
    onSettled: (data, error, variables) => {
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(variables.product_id);
        return newSet;
      });
    },
  });

  const deleteCartItemMutation = useMutation({
    mutationFn: (values) => deleteSpecificCartItem(values, customerId, platform),
    retry: false,
    onMutate: (variables) => {
      setPendingOperations(prev => new Set(prev).add(variables.id));
    },
    onSuccess: async () => {
      await syncCartListAfterMutation();
    },
    onError: () => {
      setIsSyncingCart(false);
    },
    onSettled: (data, error, variables) => {
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });
    },
  });

  const clearCartMutation = useMutation({
    mutationFn: clearCartAPI,
    retry: false,
    onSuccess: async () => {
      await syncCartListAfterMutation();
    },
    onError: () => {
      setIsSyncingCart(false);
    },
  });

  const debouncedAddToCart = useCallback((payload, debounceTime = 500, immediate = false) => {
    const productId = payload.product_id;
    
    // If immediate flag is set, process immediately without debouncing
    if (immediate) {
      // Clear any pending debounced call for this product

      if (debounceTimers.current[productId]) {
        clearTimeout(debounceTimers.current[productId]);
        delete debounceTimers.current[productId];
      }
      addToCartMutation.mutate(payload);
      return;
    }
    
    // Otherwise, use debouncing
    if (debounceTimers.current[productId]) {
      clearTimeout(debounceTimers.current[productId]);
    }

    debounceTimers.current[productId] = setTimeout(() => {
      addToCartMutation.mutate(payload);
      delete debounceTimers.current[productId];
    }, debounceTime);
  }, [addToCartMutation]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
      if (cartSyncTimerRef.current) {
        clearTimeout(cartSyncTimerRef.current);
        cartSyncTimerRef.current = null;
      }
    };
  }, []);

  const hardRefetchCart = useCallback(async () => {
    return fetchCartListQuery();
  }, [fetchCartListQuery]);

  return {
    cartData: cartData?.data || [],
    cartResponse: cartData,
    isLoadingCart,
    isFetchingCart,
    refetchCart: hardRefetchCart,
    addToCart: debouncedAddToCart,
    deleteCartItem: (id) => deleteCartItemMutation.mutate({ id }),
    clearCart: () => clearCartMutation.mutate(),
    isAddingToCart: addToCartMutation.isPending,
    isDeletingItem: deleteCartItemMutation.isPending,
    isCartMutating:
      isSyncingCart ||
      addToCartMutation.isPending ||
      deleteCartItemMutation.isPending,
    isClearingCart: clearCartMutation.isPending,
    pendingOperations,
  };
};

